import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ShoppingCart, Search, Package, Calendar, DollarSign, Truck, ArrowLeft, Filter, X, AlertCircle } from "lucide-react";

import { API } from '@/config';

const PurchaseOrdersPage = () => {
  const navigate = useNavigate();
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('po_number');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showFilters, setShowFilters] = useState(false);
  const [items, setItems] = useState([]);
  const [trucks, setTrucks] = useState([]);
  
  // Filter states
  const [filters, setFilters] = useState({
    orderDateFrom: '',
    orderDateTo: '',
    deliveryDateFrom: '',
    deliveryDateTo: '',
    suppliers: [],
    items: []
  });
  
  // Available options for filters
  const [availableSuppliers, setAvailableSuppliers] = useState([]);
  const [availableItems, setAvailableItems] = useState([]);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [itemSearch, setItemSearch] = useState('');

  useEffect(() => {
    fetchPOs();
    fetchItems();
    fetchTrucks();
  }, [filter]);

  const fetchPOs = async () => {
    try {
      const url = filter === 'all' 
        ? `${API}/warehouse/purchase-orders`
        : `${API}/warehouse/purchase-orders?status=${filter}`;
      const response = await axios.get(url);
      setPos(response.data);
      
      // Extract unique suppliers from POs
      const suppliers = [...new Set(response.data.map(po => po.vendor))].sort();
      setAvailableSuppliers(suppliers);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching POs:', error);
      setLoading(false);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/api/items`);
      setAvailableItems(response.data);
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  const fetchTrucks = async () => {
    try {
      const response = await axios.get(`${API}/api/warehouse/trucks`);
      setTrucks(response.data);
    } catch (error) {
      console.error('Error fetching trucks:', error);
    }
  };

  const getDeliveryAppointmentStatus = (po) => {
    // Find truck linked to this PO
    const truck = trucks.find(t => t.linked_po_number === po.po_number || t.po_number === po.po_number);
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Helper to safely parse YYYY-MM-DD without timezone issues
    const parseDate = (str) => {
      if (!str) return null;
      const parts = str.split('-');
      if (parts.length === 3) return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
      return new Date(str);
    };
    
    if (truck) {
      const appointmentDate = parseDate(truck.appointment_date);
      if (appointmentDate && appointmentDate < today) {
        return { hasAppointment: true, status: truck.status, date: truck.appointment_date, isPastDue: true };
      }
      return { hasAppointment: true, status: truck.status, date: truck.appointment_date, isPastDue: false };
    }
    
    // No appointment - check expected delivery
    const expectedDateStr = po.expected_delivery || po.expected_delivery_date;
    if (expectedDateStr) {
      const deliveryDate = parseDate(expectedDateStr);
      if (!deliveryDate) return { hasAppointment: false, needsFlag: false, isPastDue: false };
      deliveryDate.setHours(0, 0, 0, 0);
      const daysUntil = Math.ceil((deliveryDate - today) / (1000 * 60 * 60 * 24));
      
      if (daysUntil < 0) return { hasAppointment: false, isPastDue: true, daysOverdue: Math.abs(daysUntil) };
      if (daysUntil <= 7) return { hasAppointment: false, needsFlag: true, daysUntil, isPastDue: false };
    }
    
    return { hasAppointment: false, needsFlag: false, isPastDue: false };
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const toggleSupplier = (supplier) => {
    setFilters(prev => ({
      ...prev,
      suppliers: prev.suppliers.includes(supplier)
        ? prev.suppliers.filter(s => s !== supplier)
        : [...prev.suppliers, supplier]
    }));
  };

  const toggleItem = (itemId) => {
    setFilters(prev => ({
      ...prev,
      items: prev.items.includes(itemId)
        ? prev.items.filter(id => id !== itemId)
        : [...prev.items, itemId]
    }));
  };

  const clearFilters = () => {
    setFilters({
      orderDateFrom: '',
      orderDateTo: '',
      deliveryDateFrom: '',
      deliveryDateTo: '',
      suppliers: [],
      items: []
    });
    setSupplierSearch('');
    setItemSearch('');
  };

  const clearOrderDateFilter = () => {
    setFilters(prev => ({ ...prev, orderDateFrom: '', orderDateTo: '' }));
  };

  const clearDeliveryDateFilter = () => {
    setFilters(prev => ({ ...prev, deliveryDateFrom: '', deliveryDateTo: '' }));
  };

  const clearSupplierFilter = () => {
    setFilters(prev => ({ ...prev, suppliers: [] }));
    setSupplierSearch('');
  };

  const clearItemFilter = () => {
    setFilters(prev => ({ ...prev, items: [] }));
    setItemSearch('');
  };

  const filteredSuppliers = availableSuppliers.filter(supplier =>
    supplier.toLowerCase().includes(supplierSearch.toLowerCase())
  );

  const filteredItems = availableItems.filter(item =>
    item.sku.toLowerCase().includes(itemSearch.toLowerCase()) ||
    item.name.toLowerCase().includes(itemSearch.toLowerCase())
  );

  const hasActiveFilters = () => {
    return filters.orderDateFrom || filters.orderDateTo || 
           filters.deliveryDateFrom || filters.deliveryDateTo ||
           filters.suppliers.length > 0 || filters.items.length > 0;
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-blue-100 text-blue-700',
      partially_received: 'bg-yellow-100 text-yellow-700',
      fully_received: 'bg-green-100 text-green-700',
      closed: 'bg-gray-100 text-gray-700'
    };
    return (
      <Badge className={styles[status] || 'bg-gray-100 text-gray-700'}>
        {status.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredPOs = pos
    .filter(po => {
      // Search filter
      const matchesSearch = po.po_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           po.vendor.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Order date filter
      const matchesOrderDate = (!filters.orderDateFrom || po.order_date >= filters.orderDateFrom) &&
                               (!filters.orderDateTo || po.order_date <= filters.orderDateTo);
      
      // Delivery date filter
      const matchesDeliveryDate = (!filters.deliveryDateFrom || po.expected_delivery >= filters.deliveryDateFrom) &&
                                  (!filters.deliveryDateTo || po.expected_delivery <= filters.deliveryDateTo);
      
      // Supplier filter
      const matchesSupplier = filters.suppliers.length === 0 || filters.suppliers.includes(po.vendor);
      
      // Item filter - check if PO contains any of the selected items
      const matchesItem = filters.items.length === 0 || 
                         (po.line_items && po.line_items.some(item => filters.items.includes(item.item_id)));
      
      return matchesSearch && matchesOrderDate && matchesDeliveryDate && matchesSupplier && matchesItem;
    })
    .sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      // Handle different data types
      if (sortField === 'total_amount') {
        aVal = parseFloat(aVal);
        bVal = parseFloat(bVal);
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/warehouse')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
              <ShoppingCart className="w-8 h-8" />
              Purchase Orders
            </h1>
            <p className="text-gray-600">Inbound deliveries from vendors</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex gap-2">
            <Button
              variant={filter === 'all' ? 'default' : 'outline'}
              onClick={() => setFilter('all')}
              size="sm"
            >
              All ({pos.length})
            </Button>
            <Button
              variant={filter === 'open' ? 'default' : 'outline'}
              onClick={() => setFilter('open')}
              size="sm"
            >
              Open
            </Button>
            <Button
              variant={filter === 'partially_received' ? 'default' : 'outline'}
              onClick={() => setFilter('partially_received')}
              size="sm"
            >
              Partial
            </Button>
            <Button
              variant={filter === 'fully_received' ? 'default' : 'outline'}
              onClick={() => setFilter('fully_received')}
              size="sm"
            >
              Received
            </Button>
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search PO# or vendor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <Button
            variant={hasActiveFilters() ? "default" : "outline"}
            onClick={() => setShowFilters(!showFilters)}
            className="gap-2"
          >
            <Filter className="w-4 h-4" />
            Filters
            {hasActiveFilters() && (
              <Badge className="ml-1 bg-white text-blue-600">
                {filters.suppliers.length + filters.items.length + 
                 (filters.orderDateFrom || filters.orderDateTo ? 1 : 0) + 
                 (filters.deliveryDateFrom || filters.deliveryDateTo ? 1 : 0)}
              </Badge>
            )}
          </Button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="mt-4 p-4 border-t space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-gray-900">Filters</h3>
              {hasActiveFilters() && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1">
                  <X className="w-4 h-4" />
                  Clear All
                </Button>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Order Date Range */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">Order Date</label>
                  {(filters.orderDateFrom || filters.orderDateTo) && (
                    <button onClick={clearOrderDateFilter} className="text-xs text-blue-600 hover:text-blue-800">
                      Clear
                    </button>
                  )}
                </div>
                <div className="flex gap-2 items-center">
                  <Input
                    type="date"
                    value={filters.orderDateFrom}
                    onChange={(e) => handleFilterChange('orderDateFrom', e.target.value)}
                    className="flex-1"
                  />
                  <span className="text-gray-500">to</span>
                  <Input
                    type="date"
                    value={filters.orderDateTo}
                    onChange={(e) => handleFilterChange('orderDateTo', e.target.value)}
                    className="flex-1"
                  />
                </div>
              </div>

              {/* Expected Delivery Range */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">Expected Delivery</label>
                  {(filters.deliveryDateFrom || filters.deliveryDateTo) && (
                    <button onClick={clearDeliveryDateFilter} className="text-xs text-blue-600 hover:text-blue-800">
                      Clear
                    </button>
                  )}
                </div>
                <div className="flex gap-2 items-center">
                  <Input
                    type="date"
                    value={filters.deliveryDateFrom}
                    onChange={(e) => handleFilterChange('deliveryDateFrom', e.target.value)}
                    className="flex-1"
                  />
                  <span className="text-gray-500">to</span>
                  <Input
                    type="date"
                    value={filters.deliveryDateTo}
                    onChange={(e) => handleFilterChange('deliveryDateTo', e.target.value)}
                    className="flex-1"
                  />
                </div>
              </div>

              {/* Supplier Multi-Select */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    Supplier ({filters.suppliers.length} selected)
                  </label>
                  {filters.suppliers.length > 0 && (
                    <button onClick={clearSupplierFilter} className="text-xs text-blue-600 hover:text-blue-800">
                      Clear
                    </button>
                  )}
                </div>
                <div className="relative mb-2">
                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Search suppliers..."
                    value={supplierSearch}
                    onChange={(e) => setSupplierSearch(e.target.value)}
                    className="pl-8 text-sm"
                  />
                </div>
                <div className="border rounded-lg p-2 max-h-40 overflow-y-auto bg-gray-50">
                  {filteredSuppliers.length > 0 ? (
                    filteredSuppliers.map((supplier) => (
                      <label key={supplier} className="flex items-center gap-2 py-1 hover:bg-gray-100 px-2 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filters.suppliers.includes(supplier)}
                          onChange={() => toggleSupplier(supplier)}
                          className="rounded"
                        />
                        <span className="text-sm">{supplier}</span>
                      </label>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-2">No suppliers found</p>
                  )}
                </div>
              </div>

              {/* Item Multi-Select */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    Items ({filters.items.length} selected)
                  </label>
                  {filters.items.length > 0 && (
                    <button onClick={clearItemFilter} className="text-xs text-blue-600 hover:text-blue-800">
                      Clear
                    </button>
                  )}
                </div>
                <div className="relative mb-2">
                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Search items..."
                    value={itemSearch}
                    onChange={(e) => setItemSearch(e.target.value)}
                    className="pl-8 text-sm"
                  />
                </div>
                <div className="border rounded-lg p-2 max-h-40 overflow-y-auto bg-gray-50">
                  {filteredItems.length > 0 ? (
                    filteredItems.map((item) => (
                      <label key={item.item_id} className="flex items-center gap-2 py-1 hover:bg-gray-100 px-2 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filters.items.includes(item.item_id)}
                          onChange={() => toggleItem(item.item_id)}
                          className="rounded"
                        />
                        <span className="text-sm">{item.sku} - {item.name}</span>
                      </label>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-2">No items found</p>
                  )}
                </div>
              </div>
            </div>

            {/* Active Filters Summary */}
            {hasActiveFilters() && (
              <div className="pt-4 border-t">
                <p className="text-sm text-gray-600 mb-2">Active Filters:</p>
                <div className="flex flex-wrap gap-2">
                  {filters.orderDateFrom && (
                    <Badge variant="secondary" className="gap-1">
                      Order Date: {filters.orderDateFrom}
                      <button onClick={() => handleFilterChange('orderDateFrom', '')} className="ml-1 hover:bg-gray-300 rounded-full">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  )}
                  {filters.orderDateTo && (
                    <Badge variant="secondary" className="gap-1">
                      to {filters.orderDateTo}
                      <button onClick={() => handleFilterChange('orderDateTo', '')} className="ml-1 hover:bg-gray-300 rounded-full">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  )}
                  {filters.deliveryDateFrom && (
                    <Badge variant="secondary" className="gap-1">
                      Delivery: {filters.deliveryDateFrom}
                      <button onClick={() => handleFilterChange('deliveryDateFrom', '')} className="ml-1 hover:bg-gray-300 rounded-full">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  )}
                  {filters.deliveryDateTo && (
                    <Badge variant="secondary" className="gap-1">
                      to {filters.deliveryDateTo}
                      <button onClick={() => handleFilterChange('deliveryDateTo', '')} className="ml-1 hover:bg-gray-300 rounded-full">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  )}
                  {filters.suppliers.map(supplier => (
                    <Badge key={supplier} variant="secondary" className="gap-1">
                      Supplier: {supplier}
                      <button onClick={() => toggleSupplier(supplier)} className="ml-1 hover:bg-gray-300 rounded-full">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                  {filters.items.map(itemId => {
                    const item = availableItems.find(i => i.item_id === itemId);
                    return (
                      <Badge key={itemId} variant="secondary" className="gap-1">
                        Item: {item?.sku}
                        <button onClick={() => toggleItem(itemId)} className="ml-1 hover:bg-gray-300 rounded-full">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* PO Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('po_number')}
                  >
                    <div className="flex items-center gap-1">
                      PO Number
                      {sortField === 'po_number' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('vendor')}
                  >
                    <div className="flex items-center gap-1">
                      Vendor
                      {sortField === 'vendor' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center gap-1">
                      Status
                      {sortField === 'status' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('order_date')}
                  >
                    <div className="flex items-center gap-1">
                      Order Date
                      {sortField === 'order_date' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('expected_delivery_date')}
                  >
                    <div className="flex items-center gap-1">
                      Expected Delivery
                      {sortField === 'expected_delivery_date' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Delivery Appointment
                  </th>
                  <th 
                    className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('total_amount')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      Total Amount
                      {sortField === 'total_amount' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPOs.map((po) => (
                  <tr 
                    key={po.po_id} 
                    onClick={() => navigate(`/purchase-orders/${po.po_id}`)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="font-semibold text-gray-900">{po.po_number}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-sm font-medium text-gray-900">{po.vendor}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {getStatusBadge(po.status)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {po.order_date}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {po.expected_delivery_date}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {(() => {
                        const appointmentStatus = getDeliveryAppointmentStatus(po);
                        
                        // Past Due - highest priority indicator
                        if (appointmentStatus.isPastDue) {
                          return (
                            <div className="flex items-center gap-2">
                              <Badge className="bg-red-600 text-white flex items-center gap-1 font-semibold">
                                🚨 PAST DUE
                                {appointmentStatus.daysOverdue && 
                                  ` (${appointmentStatus.daysOverdue} day${appointmentStatus.daysOverdue !== 1 ? 's' : ''})`
                                }
                              </Badge>
                            </div>
                          );
                        }
                        
                        // Has appointment scheduled
                        if (appointmentStatus.hasAppointment) {
                          return (
                            <div className="flex items-center gap-2">
                              <Truck className="w-4 h-4 text-green-600" />
                              <Badge className="bg-green-100 text-green-700">
                                Scheduled
                              </Badge>
                            </div>
                          );
                        }
                        
                        // Due soon without appointment
                        if (appointmentStatus.needsFlag) {
                          return (
                            <div className="flex items-center gap-2">
                              <Badge className="bg-orange-100 text-orange-700 flex items-center gap-1">
                                ⚠️ Due in {appointmentStatus.daysUntil} day{appointmentStatus.daysUntil !== 1 ? 's' : ''}
                              </Badge>
                            </div>
                          );
                        }
                        
                        // No immediate action needed
                        return (
                          <span className="text-sm text-gray-400">—</span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <div className="text-sm font-bold text-gray-900">
                        ${po.total_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {filteredPOs.length === 0 && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-gray-500">
              <ShoppingCart className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <p className="text-lg font-medium mb-2">No Purchase Orders Found</p>
              <p className="text-sm">Try adjusting your filters or search</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PurchaseOrdersPage;
